import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ticketService, statusService, categoryService } from '../services/api';
import { Card, Table, StatusToggle, PageHeader, useToast, Breadcrumbs, Modal, Pagination } from './common';
import { useSidebar } from '../context/SidebarContext';
import { usePageTitle } from '../context/PageTitleContext';
import { useAuth } from '../context/AuthContext';
import useDepartment from '../hooks/useDepartment';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';

const TicketManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCollapsed } = useSidebar();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { user, getPagePermissions } = useAuth();
  const dept = useDepartment();
  // Category Type is IT's Software/Hardware split; BioMedical has no such
  // division, so the filter and the column are dropped there.
  const showCategoryType = (user?.department_code || 'IT') === 'IT';
  const permissions = getPagePermissions('/tickets');
  
  const [tickets, setTickets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketHistory, setSelectedTicketHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [summaryCards, setSummaryCards] = useState([]);
  const [activeCardFilter, setActiveCardFilter] = useState('all');
  
  // Filter states - initialize from URL params
  // const [search, setSearch] = useState(searchParams.get('search') || '');
  // const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  // const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category_id') || '');
  // const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  // const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  // const [filterBy, setFilterBy] = useState(searchParams.get('filter_by') || '');
  // const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'created_at');
  // const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'desc');

  // Filter + pagination state — persisted across navigation, cleared on hard refresh
  const [filters, setFilters] = usePageState('ticketManager', {
    currentPage:      1,
    itemsPerPage:     30,
    search:           searchParams.get('search')           || '',
    statusFilter:     searchParams.get('status')           || '',
    categoryFilter:   searchParams.get('category_id')      || '',
    categoryTypeFilter: searchParams.get('category_type')  || '',
    startDate:        searchParams.get('start_date')       || '',
    endDate:          searchParams.get('end_date')         || '',
    filterBy:         searchParams.get('filter_by')        || '',
    regionCode:       searchParams.get('region_code')      || '',
    ticketIds:        searchParams.get('ticket_ids')       || '',
    assignedUserId:   searchParams.get('assigned_user_id') || '',
    escalated:        searchParams.get('escalated')        || '',
    awaitingAssignment: searchParams.get('awaiting_assignment') || '',
    sortBy:           searchParams.get('sort_by')          || 'created_at',
    sortOrder:        searchParams.get('sort_order')       || 'desc',
  }, ['/tickets']);
  const { currentPage, itemsPerPage, search, statusFilter, categoryFilter, categoryTypeFilter,
          startDate, endDate, filterBy, regionCode, ticketIds, assignedUserId, escalated, sortBy, sortOrder,
          awaitingAssignment } = filters;
  const setCurrentPage    = (v) => setFilters({ currentPage: v });
  const setItemsPerPage   = (v) => setFilters({ itemsPerPage: v });
  const setSearch         = (v) => setFilters({ search: v });
  const setStatusFilter   = (v) => setFilters({ statusFilter: v });
  const setCategoryFilter = (v) => setFilters({ categoryFilter: v });
  const setCategoryTypeFilter = (v) => setFilters({ categoryTypeFilter: v });
  const setStartDate      = (v) => setFilters({ startDate: v });
  const setEndDate        = (v) => setFilters({ endDate: v });
  const setFilterBy       = (v) => setFilters({ filterBy: v });
  const setRegionCode     = (v) => setFilters({ regionCode: v });
  const setSortBy         = (v) => setFilters({ sortBy: v });
  const setSortOrder      = (v) => setFilters({ sortOrder: v });

  useEffect(() => {
    setPageTitle('Tickets');
    fetchStatuses();
    fetchCategories();
    fetchSummaryCards();
  }, [setPageTitle]);

  // Update filters when URL params change (e.g., from Dashboard navigation)
  // URL params take priority over sessionStorage when explicitly provided
  useEffect(() => {
    const urlSearch      = searchParams.get('search');
    const urlStatus      = searchParams.get('status');
    const urlCategory    = searchParams.get('category_id');
    const urlCategoryType = searchParams.get('category_type');
    const urlStartDate   = searchParams.get('start_date');
    const urlEndDate     = searchParams.get('end_date');
    const urlFilterBy    = searchParams.get('filter_by');
    const urlRegionCode  = searchParams.get('region_code');
    const urlTicketIds   = searchParams.get('ticket_ids');
    const urlAssignedUserId = searchParams.get('assigned_user_id');
    const urlEscalated   = searchParams.get('escalated');
    const urlSortBy      = searchParams.get('sort_by');
    const urlSortOrder   = searchParams.get('sort_order');

    // Only override if at least one URL param is explicitly set (i.e. dashboard nav)
    const hasUrlParams = [urlSearch, urlStatus, urlCategory, urlCategoryType, urlStartDate,
                          urlEndDate, urlFilterBy, urlRegionCode, urlTicketIds, urlAssignedUserId,
                          urlEscalated, urlSortBy, urlSortOrder]
                          .some(v => v !== null);
    if (!hasUrlParams) return;

    setFilters({
      search:         urlSearch      ?? '',
      statusFilter:   urlStatus      ?? '',
      categoryFilter: urlCategory    ?? '',
      categoryTypeFilter: urlCategoryType ?? '',
      startDate:      urlStartDate   ?? '',
      endDate:        urlEndDate     ?? '',
      filterBy:       urlFilterBy    ?? '',
      regionCode:     urlRegionCode  ?? '',
      ticketIds:      urlTicketIds   ?? '',
      assignedUserId: urlAssignedUserId ?? '',
      escalated:      urlEscalated   ?? '',
      sortBy:         urlSortBy      ?? 'created_at',
      sortOrder:      urlSortOrder   ?? 'desc',
      currentPage:    1,
    });
  }, [searchParams]);

  useEffect(() => {
    fetchTickets();
  }, [currentPage, search, statusFilter, categoryFilter, categoryTypeFilter, startDate, endDate, filterBy, regionCode, ticketIds, assignedUserId, escalated, sortBy, sortOrder, itemsPerPage, activeCardFilter, awaitingAssignment]);

  const fetchSummaryCards = async () => {
    try {
      const response = await ticketService.getSummaryCards();
      setSummaryCards(response.data.cards || []);
    } catch (error) {
      console.error('Error fetching summary cards:', error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await statusService.getAll({ page: 1, limit: 1000 });
      setStatuses(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll({ page: 1, limit: 1000 });
      setCategories(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (awaitingAssignment) params.awaiting_assignment = 'true';
      if (categoryFilter) params.category_id = categoryFilter;
      if (categoryTypeFilter) params.category_type = categoryTypeFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (startDate || endDate) params.date_field = 'created_at';
      if (filterBy) params.filter_by = filterBy;
      if (regionCode) params.region_code = regionCode;
      if (ticketIds) params.ticket_ids = ticketIds;
      if (assignedUserId) params.assigned_user_id = assignedUserId;
      if (escalated) params.escalated = escalated;
      if (activeCardFilter) params.card_filter = activeCardFilter;
      
      const response = await ticketService.getAll(params);
      setTickets(response.data.data);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tickets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (ticket) => {
    navigate(`/tickets/view/${ticket.id}`);
  };

  const handleEdit = (ticket) => {
    navigate(`/tickets/edit/${ticket.id}`);
  };

  const handleAddTicket = () => {
    navigate('/tickets/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this ticket?');
    if (confirmed) {
      try {
        await ticketService.delete(id);
        toast.success('Ticket deleted successfully');
        fetchTickets();
        fetchSummaryCards();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete ticket');
        toast.error(err.response?.data?.error || 'Failed to delete ticket');
        console.error(err);
      }
    }
  };

  const handleViewHistory = async (ticketId) => {
    try {
      setHistoryLoading(true);
      setSelectedTicketId(ticketId);
      setShowHistoryModal(true);
      const response = await ticketService.getHistory(ticketId);
      setSelectedTicketHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching ticket history:', error);
      setSelectedTicketHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedTicketHistory([]);
  };

  const handleDownloadHistory = () => {
    if (!selectedTicketHistory || selectedTicketHistory.length === 0) {
      toast.error('No history data to download');
      return;
    }

    // Convert history to CSV
    const csvContent = convertHistoryToCSV(selectedTicketHistory);
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const formattedTicketId = selectedTicketHistory[0]?.ticket_id || selectedTicketId;
    link.setAttribute('href', url);
    link.setAttribute('download', `ticket_${formattedTicketId}_history_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Successfully downloaded ${selectedTicketHistory.length} history record(s)`);
  };

  const convertHistoryToCSV = (data) => {
    if (!data || data.length === 0) return '';

    // Define CSV headers
    const headers = [
      'Ticket ID',
      'Date/Time',
      'User',
      'Action',
      'Field',
      'Old Value',
      'New Value'
    ];

    // Create CSV rows
    const rows = data.map(record => {
      // Format date/time safely
      const dateTime = record.created_at ? formatDateTimeDDMMYYYY(record.created_at) : '';

      return [
        record.ticket_id || '',
        `"${dateTime}"`, // Always quote date/time to prevent parsing issues
        `"${(record.user_name || 'System').replace(/"/g, '""')}"`, // Escape quotes in user name
        record.action || '',
        `"${(record.field_name || '').replace(/"/g, '""')}"`, // Escape quotes in field name
        `"${(record.old_value || '').replace(/"/g, '""')}"`, // Escape quotes in old value
        `"${(record.new_value || '').replace(/"/g, '""')}"`  // Escape quotes in new value
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Add UTF-8 BOM to prevent Excel from interpreting as SYLK
    const BOM = '\uFEFF';
    return BOM + csvContent;
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      
      // Fetch all filtered tickets without pagination
      const params = {
        page: 1,
        limit: 999999, // Large number to get all filtered tickets
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      // Apply the same filters as current view
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (awaitingAssignment) params.awaiting_assignment = 'true';
      if (categoryFilter) params.category_id = categoryFilter;
      if (categoryTypeFilter) params.category_type = categoryTypeFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (startDate || endDate) params.date_field = 'created_at';
      if (filterBy) params.filter_by = filterBy;
      if (regionCode) params.region_code = regionCode;
      if (ticketIds) params.ticket_ids = ticketIds;
      if (assignedUserId) params.assigned_user_id = assignedUserId;
      if (escalated) params.escalated = escalated;
      if (activeCardFilter) params.card_filter = activeCardFilter;

      const response = await ticketService.getAll(params);
      
      const allTickets = response.data.data;
      
      if (!allTickets || allTickets.length === 0) {
        toast.error('No tickets to download');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(allTickets);
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `tickets_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Successfully downloaded ${allTickets.length} ticket(s)`);
    } catch (err) {
      console.error('Error downloading tickets:', err);
      toast.error(err.response?.data?.error || 'Failed to download tickets');
    } finally {
      setDownloadLoading(false);
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    // Define CSV headers - avoid starting with "ID" to prevent SYLK detection
    const headers = [
      'Ticket ID',
      'Subject',
      'Description',
      'Category',
      'Category Type',
      dept.region,
      'Branch',
      'Priority',
      'Status',
      'Escalation Level',
      'Assigned To',
      'Sender Name',
      'Sender Email',
      'Created By',
      'Created Date',
      'Created Time',
      'First Response Due',
      'Responded At Date',
      'Responded At Time',
      'Resolution Due',
      'Updated By',
      'Updated Date',
      'Updated Time',
      'Resolved By',
      'Resolved Date',
      'Resolved Time',
      'Total Time Taken (Hrs)',
      'Total Time Taken (Days + Hrs + Mins)'
    ];

    // Create CSV rows
    const rows = data.map(ticket => {
      const assignedUsers = ticket.assigned_users && ticket.assigned_users.length > 0
        ? ticket.assigned_users.map(u => u.user_name).join('; ')
        : 'Unassigned';

      const createdDate = ticket.created_at ? new Date(ticket.created_at) : null;
      const updatedDate = ticket.updated_at ? new Date(ticket.updated_at) : null;
      const resolvedDate = ticket.resolved_at ? new Date(ticket.resolved_at) : null;
      const responseDue = ticket.response_due ? new Date(ticket.response_due) : null;
      const respondedAt = ticket.first_response_at ? new Date(ticket.first_response_at) : null;
      const resolutionDue = ticket.resolution_due ? new Date(ticket.resolution_due) : null;

      // Total time taken (created -> resolved)
      let totalTimeTaken = '';        // hours only, e.g. "25.5h"
      let totalTimeTakenDays = '';    // days + hours + mins, e.g. "1d 1h 30m"
      if (createdDate && resolvedDate) {
        const diffMs = resolvedDate - createdDate;
        if (diffMs >= 0) {
          const totalHours = Math.round((diffMs / 3600000) * 100) / 100;
          totalTimeTaken = `${totalHours}h`;

          const days = Math.floor(diffMs / 86400000);
          const hours = Math.floor((diffMs % 86400000) / 3600000);
          const mins = Math.floor((diffMs % 3600000) / 60000);
          const parts = [];
          if (days > 0) parts.push(`${days}d`);
          if (hours > 0) parts.push(`${hours}h`);
          parts.push(`${mins}m`);
          totalTimeTakenDays = parts.join(' ');
        }
      }

      // Get all region and branch names
      const regionNames = ticket.regions && ticket.regions.length > 0
        ? ticket.regions.map(r => r.region_name).join('; ')
        : '';
      const branchNames = ticket.branches && ticket.branches.length > 0
        ? ticket.branches.map(b => b.branch_name).join('; ')
        : '';

      return [
        ticket.ticket_id || '',
        `"${(ticket.subject || '').replace(/"/g, '""')}"`,
        `"${(ticket.description || '').replace(/"/g, '""').replace(/\n/g, ' ').replace(/\r/g, '')}"`,
        ticket.category_name || '',
        ticket.category_type || '',
        `"${regionNames}"`,
        `"${branchNames}"`,
        ticket.priority || 'P2',
        ticket.status || '',
        ticket.escalation_level || 0,
        `"${assignedUsers}"`,
        ticket.sender_name || (ticket.system_created ? 'System (Email)' : ''),
        ticket.sender_email || '',
        ticket.system_created ? 'System' : (ticket.created_by_name || ticket.created_by || ''),
        createdDate ? formatDateTimeDDMMYYYY(createdDate).split(' ')[0] : '',
        createdDate ? formatDateTimeDDMMYYYY(createdDate).split(' ')[1] : '',
        responseDue ? formatDateTimeDDMMYYYY(responseDue) : '',
        respondedAt ? formatDateTimeDDMMYYYY(respondedAt).split(' ')[0] : '',
        respondedAt ? formatDateTimeDDMMYYYY(respondedAt).split(' ')[1] : '',
        resolutionDue ? formatDateTimeDDMMYYYY(resolutionDue) : '',
        ticket.updated_by_name || ticket.updated_by || '',
        updatedDate ? formatDateTimeDDMMYYYY(updatedDate).split(' ')[0] : '',
        updatedDate ? formatDateTimeDDMMYYYY(updatedDate).split(' ')[1] : '',
        ticket.resolved_by_name || '',
        resolvedDate ? formatDateTimeDDMMYYYY(resolvedDate).split(' ')[0] : '',
        resolvedDate ? formatDateTimeDDMMYYYY(resolvedDate).split(' ')[1] : '',
        `"${totalTimeTaken}"`,
        `"${totalTimeTakenDays}"`
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');
    
    // Add UTF-8 BOM to prevent Excel from interpreting as SYLK
    const BOM = '\uFEFF';
    return BOM + csvContent;
  };

  const getStatusBadge = (statusName) => {
    // Find the status in our statuses array
    const status = statuses.find(s => s.name === statusName);
    
    if (status && status.color) {
      // Convert hex color to Tailwind-like classes
      const color = status.color;
      // For now, return a style-based badge with the dynamic color
      return {
        backgroundColor: color,
        color: getContrastColor(color),
        padding: '0.25rem 0.75rem',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        display: 'inline-block',
        width: '100px', // Fixed width for consistent sizing
        textAlign: 'center'
      };
    }
    
    // Fallback to hardcoded colors if status not found or no color
    const statusColors = {
      'Open': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Closed': 'bg-gray-100 text-gray-800',
      'Reopened': 'bg-red-100 text-red-800'
    };
    return statusColors[statusName] || 'bg-gray-100 text-gray-800';
  };

  // Helper function to determine text color based on background color
  const getContrastColor = (hexColor) => {
    // Remove # if present
    const color = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'P1': 'bg-red-100 text-red-800',
      'P2': 'bg-yellow-100 text-yellow-800',
      'P3': 'bg-green-100 text-green-800'
    };
    return priorityColors[priority] || 'bg-gray-100 text-gray-800';
  };

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handleCategoryTypeFilter = (value) => {
    setCategoryTypeFilter(value);
    setCurrentPage(1);
  };

  const handleDateRangeFilter = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCategoryFilter('');
    setCategoryTypeFilter('');
    setStartDate('');
    setEndDate('');
    setSortBy('created_at');
    setSortOrder('desc');
    setActiveCardFilter('all');
    setCurrentPage(1);
  };

  const handleCardClick = (cardKey) => {
    setActiveCardFilter(cardKey);
    setCurrentPage(1);
  };

  const getTimeRemaining = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due - now;
    if (diffMs < 0) {
      const overMs = Math.abs(diffMs);
      const overHours = Math.floor(overMs / 3600000);
      const overMins = Math.floor((overMs % 3600000) / 60000);
      return { overdue: true, text: `${overHours}h ${overMins}m overdue`, hours: overHours + overMins / 60 };
    }
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return { overdue: false, text: `${hours}h ${mins}m left`, hours: hours + mins / 60 };
  };

  return (
    <div className="admin-panel h-full min-w-0">
     
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          filters={{
            search: {
              placeholder: dept.isBioMedical
                ? 'Search by Ticket ID, Subject, Category, Entity, Branch, or Assigned User...'
                : 'Search by Ticket ID, Subject, Category, Category Type, Region, Branch, or Assigned User...',
              value: search,
              onChange: (e) => handleSearch(e.target.value)
            },
            dateRange: {
              startDate: startDate,
              endDate: endDate,
              onStartDateChange: (e) => handleDateRangeFilter(e.target.value, endDate),
              onEndDateChange: (e) => handleDateRangeFilter(startDate, e.target.value)
            },
            dropdowns: [
              {
                placeholder: 'All Categories',
                value: categoryFilter,
                onChange: (e) => handleCategoryFilter(e.target.value),
                options: categories.map((category) => ({
                  value: category.id,
                  label: category.name
                })),
                width: 'w-full sm:w-[170px]',
                searchable: true
              },
              ...(showCategoryType ? [{
                placeholder: 'All Types',
                value: categoryTypeFilter,
                onChange: (e) => handleCategoryTypeFilter(e.target.value),
                options: [
                  { value: '', label: 'All Types' },
                  { value: 'Software', label: 'Software' },
                  { value: 'Hardware', label: 'Hardware' }
                ],
                width: 'w-full sm:w-[120px]',
                searchable: false
              }] : []),
              {
                placeholder: 'All Statuses',
                value: statusFilter,
                onChange: (e) => handleStatusFilter(e.target.value),
                options: statuses.map((status) => ({
                  value: status.name,
                  label: status.name
                })),
                // Statuses are short — "In Progress" and the "All Statuses"
                // placeholder are the longest — so this stays narrower than
                // the category and type pickers beside it.
                width: 'w-full sm:w-[130px]',
                searchable: false
              }
            ]
          }}
          addButton={permissions.can_add ? {
            label: 'Add',
            onClick: handleAddTicket,
            variant: 'primary'
          } : null}
          downloadButton={permissions.can_download ? {
            label: downloadLoading ? 'Downloading...' : 'Download',
            onClick: handleDownload,
            variant: 'outline',
            className: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
            disabled: downloadLoading || loading
          } : null}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        {/* Summary Cards - Always show all escalation cards */}
        {/* {summaryCards.length > 0 && (
          <div className="flex flex-wrap gap-2 px-1 pb-3">
            {summaryCards.map((card) => {
              const isActive = activeCardFilter === card.key;
              const cardBg = isActive
                ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm';
              const bgStyle = !isActive ? { backgroundColor: `${card.color}10`, borderColor: `${card.color}40` } : {};
              return (
                <button
                  key={card.key}
                  onClick={() => handleCardClick(card.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer ${cardBg}`}
                  style={bgStyle}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: isActive ? '#fff' : card.color }}
                  />
                  <span className="font-semibold">{card.label}</span>
                  <span className={`font-bold text-base min-w-[20px] text-center ${isActive ? 'text-white' : ''}`}
                    style={{ color: isActive ? '#fff' : card.color }}
                  >
                    {card.count}
                  </span>
                </button>
              );
            })}
          </div>
        )} */}
        {summaryCards.length > 0 && (
  <div className="flex flex-wrap gap-2 px-1 pb-3">
    {summaryCards.map((card) => {
      const isActive = activeCardFilter === card.key;

      const cardBg = isActive
        ? 'text-white shadow-md'
        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm';

      const bgStyle = {
        backgroundColor: isActive ? `${card.color}CC` : `${card.color}10`,
        borderColor: `${card.color}40`
      };

      return (
        <button
          key={card.key}
          onClick={() => handleCardClick(card.key)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer ${cardBg}`}
          style={bgStyle}
        >
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: isActive ? '#fff' : card.color }}
          />

          <span className="font-semibold">{card.label}</span>

          <span
            className="font-bold text-base min-w-[20px] text-center"
            style={{ color: isActive ? '#fff' : card.color }}
          >
            {card.count}
          </span>
        </button>
      );
    })}
  </div>
)}

        <Table
          columns={[
            { 
              header: 'S.No', 
              accessor: 'serial',
              width: '60px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            {
              header: 'ID',
              accessor: 'ticket_id',
              width: '120px',
              sticky: true,
              sortable: true,
              sortKey: 'ticket_id',
              sortActive: sortBy === 'ticket_id',
              sortDirection: sortOrder,
              onSort: () => handleSort('ticket_id'),
              render: (value, row) => (
                <div className="flex items-center gap-2">
                  {row.is_unread && (
                    <span className="w-2 h-2 bg-primary-600 rounded-full" title="Unread"></span>
                  )}
                  <span className={`text-sm font-mono text-gray-900 ${row.is_unread ? 'font-bold' : ''}`}>
                    {value || `#${row.id}`}
                  </span>
                </div>
              )
            },
            { 
              header: 'Subject', 
              accessor: 'subject',
              width: '450px',
              sortable: true,
              sortKey: 'subject',
              sortActive: sortBy === 'subject',
              sortDirection: sortOrder,
              onSort: () => handleSort('subject'),
              render: (value, row) => (
                <span className={`text-sm text-gray-900 ${row.is_unread ? 'font-bold' : 'font-medium'}`} title={value}>
                  {value && value.length > 60 ? `${value.substring(0, 60)}...` : value}
                </span>
              )
            },
            { 
              header: 'Category', 
              accessor: 'category_name',
              width: '150px',
              sortable: true,
              sortKey: 'category',
              sortActive: sortBy === 'category',
              sortDirection: sortOrder,
              onSort: () => handleSort('category'),
              render: (value, row) => (
                <span className={`text-sm text-gray-700 ${row.is_unread ? 'font-bold' : ''}`}>
                  {value || '-'}
                </span>
              )
            },
            ...(showCategoryType ? [{
              header: 'Type',
              accessor: 'category_type',
              width: '100px',
              render: (value) => (
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  value === 'Software'
                    ? 'bg-blue-100 text-blue-700'
                    : value === 'Hardware'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {value || 'N/A'}
                </span>
              )
            }] : []),
            { 
              header: dept.region, 
              accessor: 'regions',
              width: '150px',
              sortable: true,
              sortKey: 'region',
              sortActive: sortBy === 'region',
              sortDirection: sortOrder,
              onSort: () => handleSort('region'),
              render: (regions, row) => {
                if (!regions || regions.length === 0) {
                  return <span className="text-sm text-gray-700">-</span>;
                }
                const regionNames = regions.map(r => r.region_name).join(', ');
                return (
                  <span className="text-sm text-gray-700" title={regionNames}>
                    {regionNames}
                  </span>
                );
              }
            },
            { 
              header: 'Branch', 
              accessor: 'branches',
              width: '150px',
              sortable: true,
              sortKey: 'branch',
              sortActive: sortBy === 'branch',
              sortDirection: sortOrder,
              onSort: () => handleSort('branch'),
              render: (branches, row) => {
                if (!branches || branches.length === 0) {
                  return <span className="text-sm text-gray-700">-</span>;
                }
                const branchNames = branches.map(b => b.branch_name).join(', ');
                return (
                  <span className="text-sm text-gray-700" title={branchNames}>
                    {branchNames}
                  </span>
                );
              }
            },
            { 
              header: 'Priority', 
              accessor: 'priority',
              width: '100px',
              sortable: true,
              sortKey: 'priority',
              sortActive: sortBy === 'priority',
              sortDirection: sortOrder,
              onSort: () => handleSort('priority'),
              render: (value) => (
                <span className={`text-xs font-semibold px-2 py-1 rounded ${getPriorityBadge(value)}`}>
                  {value || 'Medium'}
                </span>
              )
            },
            { 
              header: 'Status', 
              accessor: 'status',
              width: '120px',
              sortable: true,
              sortKey: 'status',
              sortActive: sortBy === 'status',
              sortDirection: sortOrder,
              onSort: () => handleSort('status'),
              render: (value) => {
                const badgeStyle = getStatusBadge(value);
                if (typeof badgeStyle === 'object') {
                  // Dynamic color style object
                  return (
                    <span style={badgeStyle}>
                      {value}
                    </span>
                  );
                } else {
                  // Fallback to Tailwind classes
                  return (
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${badgeStyle}`}>
                      {value}
                    </span>
                  );
                }
              }
            },
            {
              header: 'Resolved At',
              accessor: 'resolved_at',
              width: '120px',
              render: (value) => (
                <span className="text-[10px] text-gray-600">
                  {value ? formatDateTimeDDMMYYYY(value) : '—'}
                </span>
              )
            },
            {
              header: 'Escalation',
              accessor: 'escalation_level',
              width: '90px',
              render: (value) => {
                if (!value || value === 0) return <span className="text-[10px] text-gray-400">—</span>;
                return (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                    value >= 2 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    L{value}
                  </span>
                );
              }
            },
            { 
              header: 'Assigned To', 
              accessor: 'assigned_users',
              width: '150px',
              render: (value, row) => {
                // Single user assignment - show assigned_user_name or first from array
                const userName = row.assigned_user_name || (value && value.length > 0 ? value[0].user_name : null);
                
                if (!userName) {
                  return <span className="text-sm text-gray-700">—</span>;
                }
                
                return (
                  <span className="text-sm text-gray-700" title={userName}>
                    {userName}
                  </span>
                );
              }
            },
            { 
              header: 'Created', 
              accessor: 'created_at',
              width: '120px',
              sortable: true,
              sortKey: 'created_at',
              sortActive: sortBy === 'created_at',
              sortDirection: sortOrder,
              onSort: () => handleSort('created_at'),
              render: (value) => (
                <span className="text-xs text-gray-600">
                  {value ? formatDateTimeDDMMYYYY(value) : '-'}
                </span>
              )
            },
            { 
              header: 'Resolved By', 
              accessor: 'resolved_by_name',
              width: '130px',
              render: (value, row) => {
                if (!value) {
                  return <span className="text-xs text-gray-400">—</span>;
                }
                return (
                  <span className="text-xs font-medium text-gray-700">{value}</span>
                );
              }
            },
            // Only show actions column if user has edit or delete permission
            ...((permissions.can_edit || permissions.can_delete) ? [{
              header: 'Actions',
              accessor: 'actions',
              width: '120px',
              align: 'center',
              actions: [
                { type: 'history', onClick: (row) => handleViewHistory(row.id) },
                ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
              ]
            }] : [])
          ]}
          data={permissions.can_list ? tickets : []}
          onRowClick={permissions.can_view ? handleView : undefined}
          loading={loading}
          emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : undefined}
          getRowClassName={(row) => row.is_unread ? 'bg-blue-50 hover:bg-blue-100 cursor-pointer' : ''}
        />
      
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage || pagination.itemsPerPage || 30}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Card>

      {/* History Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={closeHistoryModal}
        title={`Ticket History - ${selectedTicketHistory[0]?.ticket_id || selectedTicketId}`}
        size={undefined}
        className="!w-[70vw] max-w-[70vw]"
        customHeader={
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Ticket History - {selectedTicketHistory[0]?.ticket_id || selectedTicketId}</h2>
            <div className="flex items-center gap-3">
              {permissions.can_download && (
              <button
                onClick={handleDownloadHistory}
                disabled={selectedTicketHistory.length === 0}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-600 rounded-md hover:bg-primary-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-primary-600 mr-1">↓</span>
                Download
              </button>
              )}
              <button className="text-gray-400 hover:text-gray-600 text-3xl leading-none" onClick={closeHistoryModal}>
                ×
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedTicketHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No history found for this ticket.
            </div>
          ) : (
            <Table
              columns={[
                {
                  header: 'Date/Time',
                  accessor: 'created_at',
                  width: '180px',
                  render: (value) => (
                    <span className="text-sm text-gray-900">
                      {formatDateTimeDDMMYYYY(value)}
                    </span>
                  )
                },
                {
                  header: 'User',
                  accessor: 'user_name',
                  width: '150px',
                  render: (value) => (
                    <span className="text-sm text-gray-900">
                      {value || 'System'}
                    </span>
                  )
                },
                {
                  header: 'Action',
                  accessor: 'action',
                  width: '100px',
                  render: (value) => (
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                      value === 'create' ? 'bg-green-100 text-green-800' :
                      value === 'update' ? 'bg-blue-100 text-blue-800' :
                      value === 'delete' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {value}
                    </span>
                  )
                },
                {
                  header: 'Field',
                  accessor: 'field_name',
                  width: '150px',
                  render: (value) => (
                    <span className="text-sm text-gray-900 font-medium">
                      {value || '-'}
                    </span>
                  )
                },
                {
                  header: 'Old Value',
                  accessor: 'old_value',
                  width: '200px',
                  render: (value) => (
                    <span className="text-sm text-gray-700" title={value}>
                      {value && value.length > 30 ? `${value.substring(0, 30)}...` : (value || '-')}
                    </span>
                  )
                },
                {
                  header: 'New Value',
                  accessor: 'new_value',
                  width: '200px',
                  render: (value) => (
                    <span className="text-sm text-gray-900 font-medium" title={value}>
                      {value && value.length > 30 ? `${value.substring(0, 30)}...` : (value || '-')}
                    </span>
                  )
                }
              ]}
              data={selectedTicketHistory}
              loading={false}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TicketManager;
